import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const FAQsTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'bungee',
    order: 0
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const categories = [
    { value: 'bungee', label: 'Bungee Fitness' },
    { value: 'aerial', label: 'Aerial Kids' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'general', label: 'General' }
  ];

  useEffect(() => {
    fetchFAQs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json'
    };
  };

  const fetchFAQs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/faqs`, {
        headers: getAuthHeaders(), credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId 
        ? `${API_URL}/api/admin/faqs/${editingId}`
        : `${API_URL}/api/admin/faqs`;

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(), credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchFAQs();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
    }
  };

  const handleEdit = (faq) => {
    setEditingId(faq.id);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order || 0
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/faqs/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(), credentials: 'include'
      });

      if (response.ok) {
        fetchFAQs();
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', category: 'bungee', order: 0 });
    setEditingId(null);
    setShowAddForm(false);
  };

  const filteredFaqs = filterCategory === 'all'
    ? faqs
    : faqs.filter(f => f.category === filterCategory);

  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) acc[faq.category] = [];
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="admin-faqs-tab">
      <div className="admin-section-header">
        <div>
          <h2>FAQ Management</h2>
          <p>Add, edit, and organize frequently asked questions</p>
        </div>
        <Button 
          onClick={() => setShowAddForm(true)} 
          className="admin-add-btn"
          data-testid="add-faq-btn"
        >
          <Plus size={18} />
          Add FAQ
        </Button>
      </div>

      {/* Filter */}
      <div className="faq-admin-filter">
        <select 
          value={filterCategory} 
          onChange={(e) => setFilterCategory(e.target.value)}
          className="faq-admin-select"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <span className="faq-count">{filteredFaqs.length} FAQs</span>
      </div>

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="admin-modal-overlay" onClick={resetForm}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingId ? 'Edit FAQ' : 'Add New FAQ'}</h3>
              <button className="modal-close-btn" onClick={resetForm}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="faq-admin-form">
              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="faq-admin-select"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Question</label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({...formData, question: e.target.value})}
                  placeholder="What is the question?"
                  required
                  data-testid="faq-question-input"
                />
              </div>
              <div className="form-group">
                <label>Answer</label>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({...formData, answer: e.target.value})}
                  placeholder="Write a clear, helpful answer..."
                  rows={5}
                  required
                  data-testid="faq-answer-input"
                />
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  placeholder="0"
                  min="0"
                />
                <span className="form-hint">Lower numbers appear first</span>
              </div>
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-faq-btn">
                  <Save size={16} />
                  {editingId ? 'Update FAQ' : 'Save FAQ'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAQ List */}
      {loading ? (
        <div className="admin-loading">Loading FAQs...</div>
      ) : filteredFaqs.length === 0 ? (
        <div className="admin-empty-state">
          <p>No FAQs found. Add your first FAQ to get started!</p>
        </div>
      ) : (
        <div className="faq-admin-list">
          {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category} className="faq-admin-category">
              <h4 className="faq-admin-category-title">
                {categories.find(c => c.value === category)?.label || category}
              </h4>
              <div className="faq-admin-items">
                {categoryFaqs.map((faq) => (
                  <div key={faq.id} className="faq-admin-item">
                    <div className="faq-admin-item-content">
                      <div className="faq-admin-item-order">#{faq.order || 0}</div>
                      <div className="faq-admin-item-text">
                        <h5>{faq.question}</h5>
                        <p>{faq.answer.substring(0, 150)}{faq.answer.length > 150 ? '...' : ''}</p>
                      </div>
                    </div>
                    <div className="faq-admin-item-actions">
                      <button 
                        className="faq-action-btn edit" 
                        onClick={() => handleEdit(faq)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="faq-action-btn delete" 
                        onClick={() => handleDelete(faq.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FAQsTab;
